import { Component, ChangeDetectionStrategy, AfterContentInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
//@ts-ignore
import { equals, isNil, isEmpty } from 'ramda';

import { SectionFormDirective } from '@app/classes/section-form.class';
import { formatTableData } from '@app/helpers/tables.helpers';
import { getDataValue } from '@app/helpers/object.helpers';
import { codeEqual } from '@app/helpers/code.helpers';
import Vs from '@app/services/validators/validators.service';
import { ProcessService } from '@app/services/process.service';
import { ApiService } from '@app/gateway/services/api.service';
import { ModalService } from '@app/services/modal/modal.service';
import { TokenService } from '@app/services/token/token.service';
import { MemberRow, MedAplCaseIndvData, IndividualInfo, ControlPath, SELECT_APPELLANTS_COLUMNS } from '../medical-appeals.models';

@Component({
  selector: 'nh-medical-appeal-request-info',
  templateUrl: './medical-appeal-request-info.component.html',
  styleUrls: ['./medical-appeal-request-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MedicalAppealRequestInfoComponent extends SectionFormDirective implements AfterContentInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private addressSub: Subscription;
  private isInitialLoad = true;
  
  // UI State
  showSelectAppelantsTable = false;
  enableSearch = true;
  enableTable = false;
  showFilerContactInfo = false;
  disableFilingDropdown: boolean | null = null; // null (enabled), true (disabled)
  selectedSelf = false; // true if filing for self
  showOtherMemberWarning = false; // Show warning when Other Household Member is selected

  // Data
  medAplCaseIndvData: MedAplCaseIndvData[] = [];
  selectedCaseData: MedAplCaseIndvData = null;
  selectedMember: MemberRow | null = null;
  singleCaseNum: string;
  caseNumberList: string[] = [];
  selectAppelantsRows: MemberRow[] = [];
  allIndividuals: IndividualInfo[] = []; // All individuals from selected case
  loggedInIndv: string;
  translatedLang: string;

  get multipleCase(): boolean {
    return this.medAplCaseIndvData?.length > 1;
  }

  get searchSelect(): string {
    return this.enableSearch ? 'SELECT_MEDICAL_APPEAL' : 'SELECTED';
  }

  // Constants
  readonly selectAppelantsColumns = SELECT_APPELLANTS_COLUMNS;
  readonly FileMedicalAppeal = 'FILE_MEDICAL_APPEAL';
  readonly selected = 'SELECTED';
  readonly select = 'SELECT';

  constructor(
    builder: FormBuilder,
    private readonly titleService: Title,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly processService: ProcessService,
    private readonly apiService: ApiService,
    private readonly modalService: ModalService,
    private readonly token: TokenService
  ) {
    super(builder);
    this.addressSub = this.processService.processAddressValidate.subscribe((action) => this.addressValidate(action));
    this.titleService.setTitle('Medical Appeal Request Information');
  }

  ngAfterContentInit(): void {
    this.setupTranslations();
    this.getCaseScenarios();
    // Initialize military dropdown values for prefill
    this.initializeMilitaryDropdowns();
    // Listen for address changes to reset validation flag
    const medAppealAddress = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address');
    if (medAppealAddress) {
      medAppealAddress.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.processService.addressValidated = false;
      });
    }
    this.isInitialLoad = false;
  }

  onAddressFormatChange(): void {
    const formatCode = this.getValue('data.tccMedAplRqstInfoData.medAppealAddress.addressFormat');
    const cityControl = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.city');
    if (!cityControl) return;
    if (formatCode?.code !== 'MI') {
      cityControl.setValidators([Vs.required, Vs.maCity, Vs.maxLength(25)]);
    } else {
      cityControl.clearValidators();
    }
    cityControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    const addr1Control = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.addressLine1');
    if (addr1Control) {
      addr1Control.setValidators([Vs.required, Vs.maAddressLine1, Vs.maxLength(46)]);
      addr1Control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    const addr2Control = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.addressLine2');
    if (addr2Control) {
      addr2Control.setValidators([Vs.maAddressLine2, Vs.maxLength(14)]);
      addr2Control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  ngAfterViewInit() {
    // Announce page title for NVDA screen readers
    setTimeout(() => {
      const heading = document.querySelector('h1.page-title-h1') as HTMLElement | null;
      if (heading) {
        heading.style.outline = 'none';
        heading.focus();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.addressSub) {
      this.addressSub.unsubscribe();
    }
  }

  // ========== Main Methods ==========

  getCaseScenarios(): void {
    // Load case and individual data from backend
    this.medAplCaseIndvData = this.getValue(ControlPath.MedAplCaseIndvData) || [];
    this.loggedInIndv = this.getValue(ControlPath.LoggedInIndv);
    
    if (!this.medAplCaseIndvData?.length) {
      return;
    }

    // Setup validators based on case count
    this.setupValidators();

    // Prepopulate form if backend sent previously saved values (edit mode)
    const savedData = {
      caseNum: this.getValue(ControlPath.CaseNum),
      indvId: this.getValue(ControlPath.IndvId),
      filingFor: this.getValue(ControlPath.FilingCaseFor)
    };

    if (this.multipleCase) {
      this.handleMultipleCasesScenario(savedData);
    } else {
      this.handleSingleCaseScenario(savedData);
    }

    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  private handleMultipleCasesScenario(savedData: any): void {
    this.caseNumberList = this.medAplCaseIndvData.map(row => row.caseNum);
    
    if (!savedData.caseNum) {
      return;
    }

    // Restore saved case selection (convert to string to match dropdown options)
    this.getControl(ControlPath.AppealCaseNumber).patchValue(String(savedData.caseNum));
    this.selectedCaseData = this.medAplCaseIndvData.find(
      c => String(c.caseNum) === String(savedData.caseNum)
    );
    
    if (savedData.filingFor?.code && this.selectedCaseData) {
      this.restoreSavedSelection(savedData);
    }
  }

  private handleSingleCaseScenario(savedData: any): void {
    this.showSelectAppelantsTable = true;
    this.singleCaseNum = this.medAplCaseIndvData[0].caseNum;
    this.selectedCaseData = this.medAplCaseIndvData[0];
    
    // Auto-set case number in form
    this.getControl(ControlPath.CaseNum).patchValue(Number(this.singleCaseNum));
    
    // Get all individuals for this case
    this.allIndividuals = this.getUniqueIndividuals(this.selectedCaseData.indvInfo);
    
    // Check if we should auto-select "Self"
    this.checkForSingleMemberAutoSelect();
    
    // Restore saved selection if exists
    if (savedData.filingFor?.code) {
      this.restoreSavedSelection(savedData);
    }
  }

  private restoreSavedSelection(savedData: any): void {
    this.showSelectAppelantsTable = true;
    this.enableSearch = false;
    this.allIndividuals = this.getUniqueIndividuals(this.selectedCaseData?.indvInfo);
    this.checkForSingleMemberAutoSelect();
    this.filingCaseFor();
    
    if (savedData.indvId) {
      const memberToSelect = this.selectAppelantsRows.find(
        m => String(m?.indvid) === String(savedData?.indvId)
      );
      if (memberToSelect) {
        this.selectMember(memberToSelect);
      }
    }
  }

  checkForSingleMemberAutoSelect(): void {
    // Check if only the logged-in user exists in household
    const isOnlyLoggedInUser = this.allIndividuals?.length === 1 && 
      this.isLoggedInUser(this.allIndividuals[0]?.indvId);

    if (isOnlyLoggedInUser) {
      // **SCENARIO: Only logged-in user in household**
      // Auto-select "Self" and disable dropdown
      this.autoSelectSelfOption();
      
      // Auto-load table and select the member
      this.filingCaseFor();
      
      if (this.selectAppelantsRows.length === 1) {
        this.selectMember(this.selectAppelantsRows[0]);
        
        // Update form validity after auto-selection
        setTimeout(() => {
          this.formGroup.updateValueAndValidity();
          this.cdr.detectChanges();
        }, 100);
      }
    } else {
      // Check if logged-in user is NOT among the eligible individuals
      // but other household members exist — logged-in user is not eligible
      const loggedInExists = this.allIndividuals?.some(indv => this.isLoggedInUser(indv?.indvId));
      const hasOtherMembers = this.allIndividuals?.some(indv => !this.isLoggedInUser(indv?.indvId));
      if (!loggedInExists && hasOtherMembers) {
        // **SCENARIO: Logged-in user not eligible, only other household members exist**
        // Auto-select "Other Household Member" and disable dropdown
        this.autoSelectOtherHouseholdOption();
        // Auto-load table to show other household members
        this.filingCaseFor();
      }
    }
  }

  private autoSelectOtherHouseholdOption(): void {
    const filingOptions = this.getTableCodeValuesWithSpValue('MEDAPLFILINGFOR', ControlPath.FilingCaseFor);
    const othOption = filingOptions.find(item => item.code === 'OTH');
    if (othOption) {
      this.getControl(ControlPath.FilingCaseFor).setValue(othOption);
    }
    this.disableFilingDropdown = true;
    this.selectedSelf = false;
  }

  private autoSelectSelfOption(): void {
    const filingOptions = this.getTableCodeValuesWithSpValue('MEDAPLFILINGFOR', ControlPath.FilingCaseFor);
    const selfOption = filingOptions.find(item => item.code === 'SLF');
    if (selfOption) {
      this.getControl(ControlPath.FilingCaseFor).setValue(selfOption);
    }
    this.disableFilingDropdown = true;
    this.selectedSelf = true;
  }

  private isLoggedInUser(indvId: string): boolean {
    return String(indvId) === String(this.loggedInIndv);
  }

  appealCaseFor(): void {
    const selectedCaseNum = this.getValue(ControlPath.AppealCaseNumber);
    if (!selectedCaseNum) {
      return;
    }

    // Reset all UI and form state
    this.resetCaseSelection();
    
    // Find and set selected case data
    this.selectedCaseData = this.medAplCaseIndvData.find(c => c.caseNum === selectedCaseNum);
    this.getControl(ControlPath.CaseNum).patchValue(Number(selectedCaseNum));
    
    // Get all individuals for this case
    this.allIndividuals = this.getUniqueIndividuals(this.selectedCaseData.indvInfo);
    
    this.cdr.detectChanges();
  }

  private resetCaseSelection(): void {
    this.enableSearch = true;
    this.showSelectAppelantsTable = false;
    this.enableTable = false;
    this.selectAppelantsRows = [];
    this.disableFilingDropdown = null;
    this.selectedSelf = false;
    this.showOtherMemberWarning = false;
    this.getControl(ControlPath.FilingCaseFor).reset();
    this.resetMemberValidation();
    this.hideFilerContact();
  }

  showSelectMembers(): void {
    if (!this.getValue(ControlPath.AppealCaseNumber) && this.multipleCase) {
      return;
    }

    this.showSelectAppelantsTable = true;
    this.enableSearch = false;
    
    // Check if we should auto-select based on household composition
    this.checkForSingleMemberAutoSelect();
    
    // Set validators for filing option
    this.getControl(ControlPath.FilingCaseFor).setValidators([Vs.required]);
    this.getControl(ControlPath.FilingCaseFor).updateValueAndValidity();
    
    this.cdr.detectChanges();
  }

  filingCaseFor(): void {
    const filingForValue = this.getValue(ControlPath.FilingCaseFor);
    
    if (!filingForValue || !this.allIndividuals?.length) {
      return;
    }

    // Reset UI and clear previous selection
    this.resetMemberSelection();

    const isSelfFiling = filingForValue.code === 'SLF';
    this.selectedSelf = isSelfFiling;
    
    // Show warning banner when Other Household Member is selected
    this.showOtherMemberWarning = !isSelfFiling;
    
    // Filter individuals based on filing type
    const filteredIndividuals = this.allIndividuals.filter(indv => 
      isSelfFiling 
        ? this.isLoggedInUser(indv.indvId)  // Self: show only logged-in user
        : !this.isLoggedInUser(indv.indvId) // Other: show all except logged-in user
    );
    
    this.buildMemberTable(filteredIndividuals);
    this.enableTable = true;
    
    // Auto-select ONLY for Self filing (not for Other Household Member)
    if (isSelfFiling && this.selectAppelantsRows.length === 1) {
      const memberToSelect = this.selectAppelantsRows[0];
      
      // Check eligibility before auto-selecting
      if (memberToSelect.eligForMatsAppealSw === 'Y') {
        this.selectMember(memberToSelect);
      } else {
        // Mark as selected but don't proceed - user will see ineligibility message
        memberToSelect.selected = true;
        this.selectedMember = memberToSelect;
      }
    }
    
    this.cdr.detectChanges();
  }

  private resetMemberSelection(): void {
    this.selectAppelantsRows = [];
    this.selectedMember = null;
    this.enableTable = false;
    this.hideFilerContact();
    if (!this.isInitialLoad) {
      this.resetFilerContactValues();
    }
    this.getControl(ControlPath.IndvId).reset();
    this.resetMemberValidation();
  }

  selectMember(row: MemberRow): void {
    // Check if member is ineligible
    if (row?.eligForMatsAppealSw === 'N') {
      // For ineligible members, just mark as selected but don't proceed with form
      this.selectAppelantsRows.forEach(member => member.selected = false);
      row.selected = true;
      this.selectedMember = row;
      
      // Clear any member ID to ensure form is invalid
      this.getControl(ControlPath.IndvId).reset();
      this.resetMemberValidation();
      
      // Hide filer contact and disable form progression
      this.hideFilerContact();
      
      this.cdr.detectChanges();
      return;
    }
    
    // Eligible member selected - proceed normally
    // Unselect all and select clicked row
    this.selectAppelantsRows.forEach(member => member.selected = false);
    row.selected = true;
    this.selectedMember = row;
    
    // Update form with selected member
    this.getControl(ControlPath.IndvId).patchValue(Number(row.indvid));
    this.updateMemberValidation(row.indvid);

    // Clear stale filer contact data on user-initiated member change
    if (!this.isInitialLoad) {
      this.resetFilerContactValues();
    }
    // Show/hide filer contact based on filing type
    this.toggleFilerContact();
    
    this.cdr.detectChanges();
  }

  private toggleFilerContact(): void {
    // Self filing: no filer contact needed
    // Other filing: show filer contact questions
    this.selectedSelf ? this.hideFilerContact() : this.showFilerContact();
  }

  tableCodeValue(tableName: string, tableControl: string): any {
    return this.getTableCodeValuesWithSpValue(tableName, tableControl);
  }

  /**
   * Override to ensure all table code objects include both value and spValue
   * Backend expects: { code, value, spValue }
   */
  private getTableCodeValuesWithSpValue(tableName: string, tableControl: string): any[] {
    if (!this.tables[tableName]) {
      return [];
    }

    const dataControl = this.getControl(tableControl)?.value;
    const tableObj: any[] = [];

    this.tables[tableName].forEach((element: any) => {
      const codeObj = {
        code: element.code,
        value: this.translatedLang === 'sp' ? element.spDescription : element.description,
        spValue: element.spDescription 
      };

      if (dataControl?.code === element.code && dataControl.value !== codeObj.value) {
        setTimeout(() => this.getControl(tableControl).patchValue(codeObj, { emitEvent: false }));
      }

      tableObj.push(codeObj);
    });

    return tableObj;
  }

  // ========== Private Helper Methods ==========

  private setupTranslations(): void {
    this.translatedLang = this.translateService.getDefaultLang();
    this.updateTableLanguage(this.translatedLang);
    
    this.translateService.onDefaultLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ lang }) => {
        this.translatedLang = lang;
        this.updateTableLanguage(lang);
        if (this.selectedSelf) {
          this.autoSelectSelfOption();
        } else if (this.disableFilingDropdown && !this.selectedSelf) {
          this.autoSelectOtherHouseholdOption();
        }
      });
  }

  private updateTableLanguage(language: string): void {
    const propertyKey = language === 'sp' ? 'spDescription' : 'description';
    this.tables = formatTableData(this.tables, propertyKey);
  }

  private setupValidators(): void {
    const filingControl = this.getControl(ControlPath.FilingCaseFor);
    const caseControl = this.getControl(ControlPath.AppealCaseNumber);

    if (this.multipleCase) {
      // Multiple cases: require case selection first, then filing option
      caseControl.setValidators([Vs.required]);
      filingControl.clearValidators();
    } else {
      // Single case: only require filing option
      caseControl.clearValidators();
      filingControl.setValidators([Vs.required]);
    }
    
    caseControl.updateValueAndValidity();
    filingControl.updateValueAndValidity();
  }

  private getUniqueIndividuals(indvInfo: IndividualInfo[]): IndividualInfo[] {
    // Remove duplicates based on indvId
    const uniqueMap = new Map<string, IndividualInfo>();
    indvInfo.forEach(indv => {
      if (!uniqueMap.has(indv.indvId)) {
        uniqueMap.set(indv.indvId, indv);
      }
    });
    return Array.from(uniqueMap.values());
  }

  private buildMemberTable(individuals: IndividualInfo[]): void {
    this.selectAppelantsRows = individuals.map(indv => this.createMemberRow(indv));
  }

  private createMemberRow(indv: IndividualInfo): MemberRow {
    const memberName = `${indv.firstName}${indv.midInitial ? ' ' + indv.midInitial : ''} ${indv.lastName}`;
    
    return {
      indvid: indv.indvId,
      memberName: memberName,
      personId: indv.indvId,
      selected: false,
      firstName: indv.firstName,
      lastName: indv.lastName,
      gender: indv.gender,
      dob: indv.dob,
      eligForMatsAppealSw: indv?.eligForMatsAppealSw || 'Y' // Since ISL changes are pending receiving null, defaulted to Y, remove once ISL changes are avaialble
    };
  }

  private resetFilerContactValues(): void {
    const fields = [
      ControlPath.CallerRelationship,
      ControlPath.CallerFirstName,
      ControlPath.CallerLastName,
      ControlPath.CallerPhone,
      ControlPath.CallerPhoneExt,
      ControlPath.CallerLanguage,
      ControlPath.MemberPresent,
      ControlPath.AddressFormat,
      ControlPath.AddressSame
    ];
    fields.forEach(path => this.getControl(path)?.reset());
    this.getControl(ControlPath.Address)?.reset();
  }

  private updateMemberValidation(indvId: string): void {
    const control = this.getControl(ControlPath.SelectedMember);
    control.patchValue(indvId);
    control.updateValueAndValidity();
  }

  private resetMemberValidation(): void {
    const control = this.getControl(ControlPath.SelectedMember);
    control.reset();
    control.updateValueAndValidity();
  }

  private showFilerContact(): void {
    // Reset address validation flag when showing contact fields
    this.processService.addressValidated = false;
    // Define all filer contact fields
    const filerFields = [
      ControlPath.CallerRelationship,
      ControlPath.CallerFirstName,
      ControlPath.CallerLastName,
      ControlPath.CallerPhone,
      ControlPath.CallerPhoneExt,
      ControlPath.CallerLanguage,
      ControlPath.MemberPresent,
      ControlPath.AddressSame
    ];

    // ENABLE FIELDS FIRST - before setting showFilerContactInfo = true
    // This ensures fields are enabled BEFORE the template renders
    filerFields.forEach(path => this.getControl(path).enable());
    this.getControl(ControlPath.MedAppealAddress).enable({ onlySelf: false });

    // Default address format to US if addressformat is null from response
    const addressFormatControl = this.getControl(ControlPath.AddressFormat);
    if (!addressFormatControl.value) {
      const getAddressFormat = this.tables?.ADDRESSFORMATTYPES?.find((item: any) => item.code === 'US');
      if (getAddressFormat) {
        const usFormat = {
          code: getAddressFormat.code,
          value: this.translatedLang === 'sp' ? getAddressFormat.spDescription : getAddressFormat.description,
          spValue: getAddressFormat.spDescription
        };
        addressFormatControl.patchValue(usFormat);
      }
    }

    this.onAddressFormatChange();
    
    // Set validators with configuration map
    const validatorConfig = {
      [ControlPath.CallerRelationship]: [Vs.required],
      [ControlPath.CallerFirstName]: [Vs.maxLength(45), Vs.medAplCallerFstName, Vs.required],
      [ControlPath.CallerLastName]: [Vs.maxLength(45), Vs.minLength(2), Vs.medAplCallerLastName, Vs.required],
      [ControlPath.CallerPhone]: [Vs.required, Vs.phoneNumberHOH],
      [ControlPath.CallerLanguage]: [Vs.required],
      [ControlPath.MemberPresent]: [Vs.required],
      [ControlPath.AddressFormat]: [Vs.required],
      [ControlPath.AddressSame]: [Vs.required]
    };

    // Apply validators
    Object.entries(validatorConfig).forEach(([path, validators]) => {
      this.getControl(path as ControlPath).setValidators(validators);
    });
    
    // Update validity for all fields
    [...filerFields, ControlPath.MedAppealAddress].forEach(path => {
      this.getControl(path).updateValueAndValidity();
    });
    
    // NOW show the section - fields are already enabled
    this.showFilerContactInfo = true;
    
    this.cdr.detectChanges();
  }

  private hideFilerContact(): void {
    // Set address as validated when hiding (Self filing doesn't need address validation)
    this.processService.addressValidated = true;
    this.showFilerContactInfo = false;
    
    // Define all filer contact fields
    const filerFields = [
      ControlPath.CallerRelationship,
      ControlPath.CallerFirstName,
      ControlPath.CallerLastName,
      ControlPath.CallerPhone,
      ControlPath.CallerPhoneExt,
      ControlPath.CallerLanguage,
      ControlPath.MemberPresent,
      ControlPath.AddressFormat,
      ControlPath.AddressSame
    ];

    // Disable all filer contact fields
    filerFields.forEach(path => this.getControl(path).disable());
    
    // Disable address FormGroup and all its children
    this.getControl(ControlPath.MedAppealAddress).disable({ onlySelf: false });
    
    // Clear validators for all fields
    filerFields.forEach(path => this.getControl(path).clearValidators());
    
    // Update validity for all fields
    [...filerFields, ControlPath.MedAppealAddress].forEach(path => {
      this.getControl(path).updateValueAndValidity();
    });
    
    this.cdr.detectChanges();
  }

  addressValidate(action: any): void {
    if (!this.showFilerContactInfo) {
      this.processService.addressValidated = true;
      return;
    }

    const appNum = this.applicationNumber;
    const formData = this.formGroup.value.data;
    const medAppealAddress = getDataValue('tccMedAplRqstInfoData.medAppealAddress.address', formData);
    const addressFormat = getDataValue('tccMedAplRqstInfoData.medAppealAddress.addressFormat', formData);
    const isSaveExit = !!equals(action, 'fromSaveExit');

    if (isNil(medAppealAddress) || codeEqual('MI', addressFormat)) {
      this.processService.addressValidated = true;
      return;
    }

    const postObject = {
      addressList: [
        { addressType: 'PHYSICAL', originalAddress: medAppealAddress }
      ]
    };

    if (isEmpty(postObject.addressList)) {
      this.processService.addressValidated = true;
    } else {
      this.apiService.post('applications/' + appNum + '/sections/MAREQ/address', postObject, {headers: new HttpHeaders({'pageId': 'MAREQ', 'sessionId' : this.token.getSessionId()})}, true).subscribe({
        next: (res) => {
          if (!isNil(res)) {
            this.modalService.openAddressModal(res, isSaveExit).subscribe(result => {
              if (result !== true) {
                if (result) {
                  const phyIndex = result.findIndex((i: any) => i.addressType === 'PHYSICAL');
                  if (phyIndex === 0) {
                    const addressLine1 = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.addressLine1');
                    const addressLine2 = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.addressLine2');
                    const city = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.city');
                    const state = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.state');
                    const zip = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.zip');
                    const county = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.county');

                    const currentCounty = county?.value;

                    if (result[0].selectedAddress === 'Suggested') {
                      const selectedState = this.tables.STATE.find((s: any) => s.code === result[0].suggestedAddress.state.code);
                      addressLine1.setValue(result[0].suggestedAddress.addressLine1);
                      addressLine2.setValue(result[0].suggestedAddress.addressLine2 || '');
                      city.setValue(result[0].suggestedAddress.city);
                      state.setValue(selectedState || null);
                      zip.setValue(result[0].suggestedAddress.zip);
                      if (county) {
                        county.setValue(currentCounty);
                      }
                    } else if (result[0].selectedAddress === 'Original') {
                      const selectedState = this.tables.STATE.find((s: any) => s.code === result[0].originalAddress.state.code);
                      addressLine1.setValue(result[0].originalAddress.addressLine1);
                      addressLine2.setValue(result[0].originalAddress.addressLine2 || '');
                      city.setValue(result[0].originalAddress.city);
                      state.setValue(selectedState || null);
                      zip.setValue(result[0].originalAddress.zip);
                      if (county) {
                        county.setValue(currentCounty);
                      }
                    }
                  }
                  this.processService.addressValidated = true;
                }
              }
            });
          } else {
            this.processService.addressValidated = true;
          }
        },
        error: () => {
          // If address validation API fails, allow save to proceed
          this.processService.addressValidated = true;
        }
      });
    }
  }

  onPrimaryLanguageChange(): void {
    const languageCode = this.getValue('data.tccMedAplRqstInfoData.callerPrimLangCd')?.code;
    if (languageCode !== 'OT') {
      this.getControl('data.tccMedAplRqstInfoData.callerPrimLangOther').reset();
    }
  }

  private initializeMilitaryDropdowns(): void {
    const addrApoFpoControl = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.addrApoFpo');
    if (addrApoFpoControl?.value?.code && this.tables?.MILITARYPOCODES) {
      const matchingValue = this.tables.MILITARYPOCODES.find(
        (item: any) => item.code === addrApoFpoControl.value.code
      );
      if (matchingValue) {
        addrApoFpoControl.patchValue(matchingValue);
      }
    }

    const addrAaAeApControl = this.getControl('data.tccMedAplRqstInfoData.medAppealAddress.address.addrAaAeAp');
    if (addrAaAeApControl?.value?.code && this.tables?.STATEMILITARY) {
      const matchingValue = this.tables.STATEMILITARY.find(
        (item: any) => item.code === addrAaAeApControl.value.code
      );
      if (matchingValue) {
        addrAaAeApControl.patchValue(matchingValue);
      }
    }
  }
}